import {
    of,
    switchMap,
    BehaviorSubject,
    map,
    tap,
    fromEvent,
    merge,
    first,
    distinctUntilChanged,
    finalize,
    combineLatest,
    catchError,
} from 'rxjs';

import { replaceViewContainer, renderTemplate } from './view.js';
import { dayOfWeek, formatDateHuman } from './date.js';
import { createDishSelect } from './dish-select.js';
import { dishes$, planDish, recommendDishes, formatApiErrorMessage } from './api.js';
import { syncInto, showErrorBanner } from './dom.js';

export function createAddDishView(day) {
    return of(undefined).pipe(
        switchMap(() => {
            let root = renderTemplate('add-dish-view');
            root.querySelector('.add-dish-title').textContent =
                `Gericht planen für ${dayOfWeek[day.getDay()]}`;
            root.querySelector('.date').textContent = formatDateHuman(day);
            replaceViewContainer(root);
            let dishList$ = recommendDishes(day).pipe(
                map((dishIds) =>
                    dishIds.map((dId) => ({
                        id: dId,
                    }))
                ),
                catchError((err) => {
                    showErrorBanner(root, formatApiErrorMessage(err));
                    throw err;
                })
            );

            let selectedDishId$ = new BehaviorSubject(null);

            let saving$ = new BehaviorSubject(false);

            let updateSelectedDish$ = createDishSelect({
                container: root.querySelector('.dish-select-container'),
                dishList$,
                selectedDishId$,
                disabled$: saving$,
            }).pipe(
                tap((selectedDishId) => {
                    selectedDishId$.next(selectedDishId);
                })
            );

            let saveDisabled$ = combineLatest([
                saving$,
                selectedDishId$.pipe(map((selectedDishId) => !!selectedDishId)),
            ]).pipe(
                map(([saving, dishSelected]) => saving || !dishSelected),
                syncInto(root.querySelector('.save'), 'disabled')
            );

            let save$ = fromEvent(root.querySelector('.save'), 'click').pipe(
                switchMap(() => {
                    saving$.next(true);
                    return of(undefined).pipe(
                        switchMap(() => selectedDishId$.pipe(first())),
                        switchMap((dishId) => planDish(day, dishId)),
                        tap(() => history.back()),
                        finalize(() => saving$.next(false))
                    );
                })
            );

            let newDishDisabled$ = saving$.pipe(
                syncInto(root.querySelector('.new-dish'), 'disabled')
            );

            let newDish$ = fromEvent(root.querySelector('.new-dish'), 'click').pipe(
                tap(() => {
                    window.location.hash = '#/create-dish';
                })
            );

            let cancelDisabled$ = saving$.pipe(syncInto(root.querySelector('.cancel'), 'disabled'));

            let cancel$ = fromEvent(root.querySelector('.cancel'), 'click').pipe(
                tap((event) => {
                    history.back();
                })
            );

            return merge(
                updateSelectedDish$,
                saveDisabled$,
                save$,
                newDish$,
                newDishDisabled$,
                cancelDisabled$,
                cancel$
            );
        })
    );
}
